import React, { useRef, useState, useEffect, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import axios from 'axios';
import { useAuth } from '../components/AuthContext';

const initialForm = { tipoReclamoId: 0, nombre: '' };

const TiposReclamo = () => {
    const toast    = useRef(null);
    const HOST_API = process.env.REACT_APP_URL;
    const { administracionId } = useAuth();

    const [tipos,    setTipos]    = useState([]);
    const [loading,  setLoading]  = useState(false);
    const [visible,  setVisible]  = useState(false);
    const [saving,   setSaving]   = useState(false);
    const [formData, setFormData] = useState(initialForm);

    const fetchTipos = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${HOST_API}api/tiposReclamo/GetTiposReclamo?administracionId=${administracionId}`);
            setTipos(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los tipos', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [HOST_API, administracionId]);

    useEffect(() => { fetchTipos(); }, [fetchTipos]);

    const handleNuevo = () => { setFormData(initialForm); setVisible(true); };
    const handleEditar = (row) => { setFormData({ tipoReclamoId: row.tipoReclamoId, nombre: row.nombre }); setVisible(true); };

    const handleEliminar = (row) => {
        confirmDialog({
            message: `¿Eliminar el tipo "${row.nombre}"?`,
            header: 'Eliminar tipo de reclamo',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sí, eliminar',
            rejectLabel: 'No',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.delete(`${HOST_API}api/tiposReclamo/DeleteTipoReclamo?tipoReclamoId=${row.tipoReclamoId}&administracionId=${administracionId}`);
                    fetchTipos();
                    toast.current.show({ severity: 'info', summary: 'Eliminado', detail: 'Tipo eliminado', life: 2500 });
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar el tipo', life: 3000 });
                }
            },
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.nombre.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Ingresá el nombre del tipo', life: 3000 });
            return;
        }
        setSaving(true);
        try {
            await axios.post(`${HOST_API}api/tiposReclamo/SaveTipoReclamo`, {
                tipoReclamoId:    formData.tipoReclamoId,
                administracionId,
                nombre:           formData.nombre.trim(),
            });
            setVisible(false);
            fetchTipos();
            toast.current.show({ severity: 'success', summary: 'Guardado', detail: 'Tipo guardado correctamente', life: 2500 });
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar el tipo', life: 3000 });
        } finally {
            setSaving(false);
        }
    };

    const accionesTemplate = (row) => (
        <div style={{ display: 'flex', gap: '0.25rem' }}>
            <Button icon="pi pi-pencil" rounded text severity="info"   tooltip="Editar"    tooltipOptions={{ position: 'left' }} onClick={() => handleEditar(row)} />
            <Button icon="pi pi-trash"  rounded text severity="danger" tooltip="Eliminar"  tooltipOptions={{ position: 'left' }} onClick={() => handleEliminar(row)} />
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />

            {loading && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
                </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                <div>
                    <h4 style={{ margin: 0, color: '#101828' }}>Tipos de reclamo</h4>
                    <p style={{ margin: '0.3rem 0 0', color: '#667085', fontSize: '0.88rem' }}>Configuración de categorías de reclamos</p>
                </div>
                <Button label="Nuevo tipo" icon="pi pi-plus" onClick={handleNuevo} />
            </div>

            <DataTable
                value={tipos}
                emptyMessage="No hay tipos de reclamo configurados"
                style={{ fontSize: '0.88rem' }}
            >
                <Column field="nombre" header="Nombre" sortable />
                <Column header="" body={accionesTemplate} style={{ width: 90, textAlign: 'center' }} />
            </DataTable>

            <Dialog
                header={formData.tipoReclamoId === 0 ? 'Nuevo tipo de reclamo' : 'Editar tipo de reclamo'}
                visible={visible}
                style={{ width: 400 }}
                modal
                onHide={() => setVisible(false)}
            >
                <form onSubmit={handleSubmit}>
                    <div className="flex flex-column gap-3 mt-2">
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' }}>
                                Nombre <span style={{ color: '#f04438' }}>*</span>
                            </label>
                            <InputText
                                value={formData.nombre}
                                onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                                className="w-full"
                                placeholder="Ej: Luminarias, Agua, Seguridad…"
                                maxLength={100}
                                autoFocus
                            />
                        </div>
                        <div className="flex justify-content-end gap-2 mt-1">
                            <Button label="Cancelar" type="button" severity="secondary" text onClick={() => setVisible(false)} />
                            <Button label={saving ? 'Guardando…' : 'Guardar'} icon={saving ? undefined : 'pi pi-check'} type="submit" disabled={saving} />
                        </div>
                    </div>
                </form>
            </Dialog>
        </>
    );
};

export default TiposReclamo;
